import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod, resolveComparePeriod } from '../../components/performance/pmAnalyticsFilters';
import {
  Badge, Grid, PageEmpty, PageWrap, TabButton, Tabs,
} from '../performance-operations/styles';
import {
  anyStale, fetchPeriods, oldestCalculatedAt,
} from '../performance-operations/config';
import { processHealthHref } from '../../components/performance/processHealthLinks';
import { ActionTrackingPanel, AiInsightsPanel } from '../performance-operations/components/InsightPanels';
import ResultKpiCards from '../performance-results/shared/ResultKpiCards';
import DistributionPanel from '../performance-results/shared/DistributionPanel';
import {
  EXECUTIVE_KPI_CODES, Q, resultsLink, usePageParams,
} from '../performance-results/config';
import OrgSummaryPanel from './components/OrgSummaryPanel';

// PM analitik P2 — E01 Yönetim Panosu (CHRO / CEO görünümü, /performance/executive). Yalnız İK (rota + menü
// requiresEnterpriseAdmin; birim uçları da sunucuda İK'ya kapalı → yönetici şirket toplamına erişemez). Analizin "CHRO
// görünümü": en çok 6 özet kart (M01, M02, M06, M09, M07, M08) + kritik birim; ortada performans dağılımı (sıralı sütun) ve
// hedef gerçekleşme bantları, sağda en çok 3 AI bulgusu; altta organizasyon özeti ve öncelikli 5 aksiyon. Final/ön sonuç
// rozeti; ön sonuç ayrı görünümdür (finalle birleşmez). Detaya iniş: tamamlanma → E02, bant → E03 listesi, birim → E10,
// AI → E15, kapsam → dönem katılımcı listesi. İK Operasyon Panosu (/performance/operations) ayrı görünümdür.
const StateBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export default function PerformanceExecutivePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [params, setParams] = usePageParams([Q.prelim]);
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);
  const onChanged = useCallback(() => setRefreshKey((k) => k + 1), []);

  const prelim = params[Q.prelim] === '1';
  const comparePeriod = useMemo(() => resolveComparePeriod(periods, filters), [periods, filters]);
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);

  // KPI → detay: kapsam → dönem katılımcı listesi; tamamlanma → E02 final aşaması (E02 açıkken); puan/oranlar → E03.
  const kpiHref = (code) => {
    switch (code) {
      case 'M01': return `/performance/periods/${filters.periodId}`;
      // Süreç Takibi Özet'i, aşama kartı "Nihai Sonuç"ta ve ortak süzgeçlerle (Plan 4 bağlantı sözleşmesi).
      case 'M02': return processHealthHref(filters, { m03Stage: 'final_result' });
      case 'M07': return resultsLink(filters, { [Q.band]: 'high' });
      case 'M08': return resultsLink(filters, { [Q.band]: 'low' });
      default: return resultsLink(filters);
    }
  };

  // Dağılım çubuğu → E03'te aynı süzgeçli çalışan listesi (bant / seviye / hedef bandı).
  const openList = (kind, bucket) => {
    const extra = kind === 'bands' ? { [Q.band]: bucket.key }
      : kind === 'goalBands' ? { [Q.goalBand]: bucket.key }
        : { [Q.bucket]: bucket.key };
    history.push(resultsLink(filters, { ...extra, ...(prelim ? { [Q.prelim]: '1' } : {}) }));
  };

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t('performance.executive.title') }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.results.analytics.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={oldestCalculatedAt(metas)}
          stale={anyStale(metas)}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <StateBar>
          <Badge $tone={prelim ? 'info' : 'success'} data-score-state={prelim ? 'preliminary' : 'final'}>
            {prelim ? t('performance.results.analytics.badge.preliminary') : t('performance.results.analytics.badge.final')}
          </Badge>
          <Tabs role="tablist" style={{ marginBottom: 0 }}>
            {['final', 'preliminary'].map((s) => (
              <TabButton
                key={s} role="tab" aria-selected={(s === 'preliminary') === prelim} $active={(s === 'preliminary') === prelim}
                onClick={() => setParams({ [Q.prelim]: s === 'preliminary' ? '1' : null })} data-score-tab={s}
              >
                {t(`performance.results.analytics.scoreState.${s}`)}
              </TabButton>
            ))}
          </Tabs>
        </StateBar>
      ) : null}
      {ready ? (
        <Grid key={filters.periodId}>
          <ResultKpiCards
            filters={filters} codes={EXECUTIVE_KPI_CODES} comparePeriod={comparePeriod} onMeta={onMeta} span={2} kpi
            hrefOf={kpiHref}
          />
          <DistributionPanel
            filters={filters} panelKey="distribution" span={5} prelim={prelim}
            title={prelim ? t('performance.results.analytics.dist.titlePreliminary') : t('performance.executive.distribution')}
            kinds={prelim ? ['levels'] : ['levels', 'bands']} comparePeriod={comparePeriod}
            onSelect={openList} canShowPreliminary onShowPreliminary={() => setParams({ [Q.prelim]: '1' })} onMeta={onMeta}
          />
          <DistributionPanel
            filters={filters} panelKey="goalBands" span={3} prelim={prelim}
            title={t('performance.executive.goalBands')} kinds={['goalBands']} comparePeriod={comparePeriod}
            onSelect={openList} onMeta={onMeta}
          />
          <AiInsightsPanel filters={filters} refreshKey={refreshKey} onChanged={onChanged} />
          <OrgSummaryPanel filters={filters} comparePeriod={comparePeriod} onMeta={onMeta} span={8} />
          <ActionTrackingPanel filters={filters} refreshKey={refreshKey} />
        </Grid>
      ) : null}
    </PageWrap>
  );
}
